import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Image, ExternalLink } from 'lucide-react';
import { ResponsiveEmbed } from './ResponsiveEmbed';
import { PlatformKey } from '@/utils/platformConfig';

interface EmbedWithPlaceholderProps {
  platform: PlatformKey;
  input: string;
  url?: string | null;
  className?: string;
  deferLoad?: boolean;
  aspect?: number;
}


export const EmbedWithPlaceholder: React.FC<EmbedWithPlaceholderProps> = ({
  platform,
  input,
  url,
  className = '',
  deferLoad = false,
  aspect = 1.777,
}) => {
  const [hasError, setHasError] = useState(false);
  const embedRef = useRef<HTMLDivElement>(null);

  // Stabilize handlers to prevent unnecessary re-renders
  const handleError = useCallback(() => setHasError(true), []);
  const handleLoad = useCallback(() => {}, []);

  useEffect(() => {
    // Reset states when input changes
    setHasError(false);
  }, [input]);

  // ---- brand-y bits ----
  const [iconIdx, setIconIdx] = React.useState(0);

  // Extract domain info for favicon
  let domain = '';
  let origin = '';
  let path = '';
  if (platform === 'link' && url) {
    try {
      const u = new URL(url);
      domain = u.hostname.replace(/^www\./, '');
      origin = u.origin;
      path = decodeURIComponent((u.pathname + u.search).replace(/^\/?/, '/')) || '/';
    } catch {
      // leave empty; we still render a safe card
    }
  }

  const faviconCandidates = React.useMemo(() => {
    if (!domain) return [];
    return [
      // High quality, very reliable
      `https://www.google.com/s2/favicons?domain=${domain}&sz=128`,
      `https://icons.duckduckgo.com/ip3/${domain}.ico`,
      // Site-local fallbacks
      `${origin}/favicon.ico`,
      `${origin}/favicon.png`,
      `${origin}/apple-touch-icon.png`,
      `${origin}/apple-touch-icon-precomposed.png`,
    ].filter(Boolean);
  }, [domain, origin]);

  if (platform === 'link' && url) {

    const faviconSrc = faviconCandidates[iconIdx];

    return (
      <div className="flex items-center justify-center w-full h-full">
        <div className="extlink-card">
          <div className="extlink-stack" onClick={(e) => e.stopPropagation()}>
            {/* Favicon bubble */}
            <div className="extlink-icon">
              {faviconSrc ? (
                <img
                  src={faviconSrc}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  decoding="async"
                  onError={() => {
                    if (iconIdx < faviconCandidates.length - 1) setIconIdx(iconIdx + 1);
                  }}
                />
              ) : (
                <ExternalLink className="w-8 h-8" aria-hidden="true" />
              )}
            </div>

            {/* Brand + URL */}
            <div className="extlink-meta">
              <div className="extlink-domain" title={origin || url}>
                {domain || 'External site'}
              </div>
              <div className="extlink-path" title={url}>
                {path}
              </div>
            </div>

            {/* CTA */}
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="extlink-button"
              aria-label={`Open ${domain || 'link'} in a new tab`}
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="w-4 h-4" />
              Open
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (hasError) {
    // Show placeholder when embed fails
    return (
      <div className="embed-placeholder">
        <div className="embed-placeholder-content">
          <Image className="embed-placeholder-icon" />
          <p className="embed-placeholder-text">Content Preview Unavailable</p>
          <p className="embed-placeholder-subtext">
            The embed couldn't be loaded, but your content is saved.
          </p>
          {url && (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-block px-6 py-3 bg-[var(--accent-primary)] text-white rounded-lg border border-[color-mix(in_srgb,var(--accent-primary) 65%, transparent)] hover:bg-[color-mix(in_srgb,var(--accent-primary) 85%, #ffffff 15%)] transition-colors font-medium"
              onClick={(e) => e.stopPropagation()}
            >
              View Original
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      role="region"
      aria-label={`${platform} embed`}
      ref={embedRef}
      className={className}
    >
      <ResponsiveEmbed
        platform={platform}
        input={input}
        className="w-full h-full"
        onError={handleError}
        onLoad={handleLoad}
        deferLoad={deferLoad}
        aspect={aspect}
      />
    </div>
  );
};

// Memoize component to prevent re-renders unless embed-relevant props change
export default React.memo(EmbedWithPlaceholder, (a, b) =>
  a.platform === b.platform &&
  a.input === b.input &&
  a.url === b.url
);

